import { HubConnectionState } from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import { ComponentProps, useCallback, useState } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { CancelButton } from 'src/components/Buttons';
import { DownloadButton } from 'src/components/Buttons/DownloadButton';
import {
  bulkEditInProgress,
  BulkEditStatus,
} from 'src/components/common/BulkActions/BulkEditStatus';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useBulkEditHubContext } from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { DnDFileUploader } from 'src/core/POS/DnDFileUploader';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button, Stack } from 'src/core/ui';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { ContentId } from 'src/utils/constants/contentId';
import { downloadFileFromBlob } from 'src/utils/fileUtils';
import {
  ListingCatalogMetricsQueryKey,
  ListingCatalogQueryKey,
} from 'src/utils/inventoryUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditListingClient,
  BulkEditProgress,
  BulkEditStep,
  ListingClient,
} from 'src/WebApiController';

import { BulkEditHeader } from '../common/BulkEditHeader';

export const SEAT_SAVERS_BULK_UPLOAD_KEY = 'SEAT_SAVERS_BULK_UPLOAD';

export type BulkUploadSeatSaversDialogProps = ComponentProps<typeof RSModal> & {
  onCancel: () => void;
};

export function BulkUploadSeatSaversDialog({
  onCancel,
  ...rest
}: BulkUploadSeatSaversDialogProps) {
  const queryClient = useQueryClient();

  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | undefined>();
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const { setActivePosEntity } = useActivePosEntityContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
    refreshCatalog,
  } = useCatalogDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();

  const onBulkEditProgressDone = useCallback(async () => {
    setActivePosEntity(0);
    queryClient.refetchQueries({
      queryKey: [ListingCatalogQueryKey],
      type: 'all',
    });
    queryClient.refetchQueries({
      queryKey: [ListingCatalogMetricsQueryKey],
      type: 'all',
    });

    if (refreshExpandedListItems) {
      await refreshCatalog();
      await refreshExpandedListItems();
    }
  }, [
    queryClient,
    refreshCatalog,
    refreshExpandedListItems,
    setActivePosEntity,
  ]);

  const onCancelDialog = useCallback(async () => {
    setIsRefreshing(true);
    setActivePosEntity(0);
    if (refreshExpandedListItems) {
      await refreshCatalog();
      await refreshExpandedListItems();
    }
    setIsRefreshing(false);

    onCancel();
  }, [onCancel, refreshCatalog, refreshExpandedListItems, setActivePosEntity]);

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }
        onCancelDialog();
      }
    },
    [
      onCancelDialog,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        await onBulkEditProgressDone();
        onClose(doneProgress, true);
      }
    },
    [onBulkEditProgressDone, onClose]
  );

  const { bulkEditHub, progress, errors, warnings, initJob } = useBulkEditHub(
    ActionOutboxEntityType.Listing,
    BulkActionType.UploadSeatSaversFile,
    SEAT_SAVERS_BULK_UPLOAD_KEY,
    onBulkEditDone
  );

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const onUploadSeatSaversFile = useCallback(() => {
    if (!uploadedFile) {
      return;
    }

    setIsLoading(true);
    tryInvokeApi(
      async () => {
        const result = await new BulkEditListingClient(
          activeAccountWebClientConfig
        ).importSeatSaversFromFile(
          SEAT_SAVERS_BULK_UPLOAD_KEY,
          hasBackgroundBulkEditFeature,
          {
            data: uploadedFile,
            fileName: uploadedFile!.name,
          }
        );

        if (!hasBackgroundBulkEditFeature) {
          if (result.success) {
            onCancel();
          } else {
            showErrorDialog(
              'BulkEditListingClient.importSeatSaversFromFile',
              {
                message: result.errors.join('\n'),
              },
              {
                trackErrorData: { uploadedFile },
              }
            );
          }
        }
      },
      (error) => {
        showErrorDialog(
          'BulkEditListingClient.importSeatSaversFromFile',
          error,
          {
            trackErrorData: { uploadedFile },
          }
        );
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    hasBackgroundBulkEditFeature,
    onCancel,
    showErrorDialog,
    uploadedFile,
  ]);

  const onDownloadTemplateClick = useCallback(() => {
    tryInvokeApi(
      async () => {
        const file = await new ListingClient(
          activeAccountWebClientConfig
        ).downloadSeatSaversFileTemplate();

        if (file) {
          downloadFileFromBlob(
            file.fileName ?? 'placeholders-template.csv',
            file.data
          );
        }
      },
      (error) => {
        showErrorDialog('ListingClient.downloadSeatSaversFileTemplate', error);
      }
    );
  }, [activeAccountWebClientConfig, showErrorDialog]);

  return (
    <>
      <GenericDialog
        {...rest}
        size="md"
        onOpened={() => {
          initJob();
        }}
        onClosed={() => {
          setMainDialogOpened(false);
          if (progress) {
            setSelectionMode(undefined);
          }
          rest.onClosed?.();
        }}
        header={
          <BulkEditHeader
            headerText={<Content id={ContentId.UploadSeatSaversFile} />}
          />
        }
        footer={
          <>
            <CancelButton
              onClick={onCancelDialog}
              disabled={isLoading || isRefreshing}
              textContentId={
                hasBackgroundBulkEditFeature && progress
                  ? ContentId.Close
                  : ContentId.Cancel
              }
            />
            {(progress?.step !== BulkEditStep.Done ||
              Boolean(progress?.failureCount)) && (
              <Button
                disabled={
                  uploadedFile == null ||
                  isRefreshing ||
                  bulkEditInProgress(
                    hasBackgroundBulkEditFeature,
                    isLoading,
                    progress
                  )
                }
                onClick={() => {
                  onUploadSeatSaversFile();
                }}
              >
                {progress?.failureCount ? (
                  <Content id={ContentId.Retry} />
                ) : (
                  <Content id={ContentId.Save} />
                )}
              </Button>
            )}
          </>
        }
        onCancel={isLoading ? undefined : onCancelDialog}
      >
        <BulkEditStatus
          entityType={ActionOutboxEntityType.Listing}
          isLoading={isLoading}
          updateKey={SEAT_SAVERS_BULK_UPLOAD_KEY}
        >
          {isLoading ? (
            <PosSpinner />
          ) : (
            <Stack direction="column" gap="m">
              <DownloadButton
                disabled={isRefreshing || isLoading}
                variant={'outline'}
                textContentId={ContentId.DownloadTemplate}
                onClick={onDownloadTemplateClick}
              />
              <DnDFileUploader
                disabled={isRefreshing || isLoading}
                onChange={(files) => files[0] && setUploadedFile(files[0])}
                multiple={false}
                acceptedFileTypes={['.csv']}
              />
            </Stack>
          )}
        </BulkEditStatus>
      </GenericDialog>
    </>
  );
}
